import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import {
  GeneratedStatement,
  StatementSection,
  StatementFilters,
  SurveyMetadata
} from '../../models/statement-generator.models';
import * as XLSX from 'xlsx';

/**
 * Statement Review Component
 * Displays generated statements with full details and selection capabilities
 */
@Component({
  selector: 'app-statement-review',
  templateUrl: './statement-review.component.html',
  styleUrls: ['./statement-review.component.scss'],
  standalone: false
})
export class StatementReviewComponent implements OnInit {
  @Input() statements: GeneratedStatement[] = [];
  @Input() sections: StatementSection[] = [];
  @Input() surveyMetadata: SurveyMetadata | null = null;
  @Input() projectTitle = '';
  @Input() selectedStatementIds: number[] = [];

  @Output() selectionChange = new EventEmitter<number[]>();
  @Output() addToSurvey = new EventEmitter<number[]>();
  @Output() exportStatements = new EventEmitter<GeneratedStatement[]>();

  // Filtering and sorting
  filters: StatementFilters = {};
  searchText = '';
  selectedSection = 'all';
  selectedEvidenceLevel = 'all';
  selectedComplianceRisk = 'all';
  showOnlySelected = false;

  // View mode
  viewMode: 'cards' | 'table' = 'cards';

  // Expanded statement for details
  expandedStatementId: number | null = null;

  ngOnInit(): void {
    console.log('Statement Review initialized with', this.statements.length, 'statements');
  }

  /**
   * Get filtered and sorted statements
   */
  get filteredStatements(): GeneratedStatement[] {
    let filtered = [...this.statements];

    // Apply filters
    if (this.selectedSection !== 'all') {
      filtered = filtered.filter(s => s.section === this.selectedSection);
    }

    if (this.selectedEvidenceLevel !== 'all') {
      filtered = filtered.filter(s => s.level_of_evidence === this.selectedEvidenceLevel);
    }

    if (this.selectedComplianceRisk !== 'all') {
      filtered = filtered.filter(s => {
        const risk = s.compliance?.off_label_risk || (s.compliance as any)?.off_label_tone_risk_flag;
        return risk === this.selectedComplianceRisk;
      });
    }

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(s =>
        s.statement_text?.toLowerCase().includes(search) ||
        s.rationale?.toLowerCase().includes(search) ||
        s.why_it_matters?.toLowerCase().includes(search)
      );
    }

    if (this.showOnlySelected) {
      filtered = filtered.filter(s => this.selectedStatementIds.includes(s.id));
    }

    return filtered;
  }

  /**
   * Get unique sections for filter
   */
  get uniqueSections(): string[] {
    const sections = new Set(this.statements.map(s => s.section));
    return Array.from(sections);
  }

  /**
   * Get unique evidence levels for filter
   */
  get uniqueEvidenceLevels(): string[] {
    const levels = new Set(this.statements.map(s => s.level_of_evidence));
    return Array.from(levels);
  }

  /**
   * Check if statement is selected
   */
  isSelected(statementId: number): boolean {
    return this.selectedStatementIds.includes(statementId);
  }

  /**
   * Toggle statement selection
   */
  toggleSelection(statementId: number): void {
    const statement = this.statements.find(s => s.id === statementId);
    if (statement?.is_in_draft_survey) return;

    const index = this.selectedStatementIds.indexOf(statementId);
    let newSelection: number[];

    if (index > -1) {
      newSelection = this.selectedStatementIds.filter(id => id !== statementId);
    } else {
      newSelection = [...this.selectedStatementIds, statementId];
    }

    this.selectionChange.emit(newSelection);
  }

  /**
   * Select all filtered statements
   */
  selectAll(): void {
    const allIds = this.filteredStatements.filter(s => !s.is_in_draft_survey).map(s => s.id);
    const newSelection = [...new Set([...this.selectedStatementIds, ...allIds])];
    this.selectionChange.emit(newSelection);
  }

  /**
   * Deselect all
   */
  deselectAll(): void {
    this.selectionChange.emit([]);
  }

  /**
   * Toggle expanded view for a statement
   */
  toggleExpanded(statementId: number): void {
    this.expandedStatementId = this.expandedStatementId === statementId ? null : statementId;
  }

  /**
   * Check if statement is expanded
   */
  isExpanded(statementId: number): boolean {
    return this.expandedStatementId === statementId;
  }

  /**
   * Handle add to survey
   */
  handleAddToSurvey(): void {
    if (this.selectedStatementIds.length === 0) {
      return;
    }
    this.addToSurvey.emit(this.selectedStatementIds);
  }

  /**
   * Handle export (legacy emit)
   */
  handleExport(): void {
    const selectedStatements = this.statements.filter(s =>
      this.selectedStatementIds.includes(s.id)
    );
    this.exportStatements.emit(selectedStatements);
  }

  /**
   * Export selected statements to Excel (.xlsx)
   */
  exportSelectedToExcel(): void {
    const selected = this.statements.filter(s => this.selectedStatementIds.includes(s.id));
    if (selected.length === 0) return;

    const rows = selected.map(s => ({
      ID: s.id,
      Statement: s.statement_text || '',
      Section: s.section || '',
      Rationale: s.rationale || '',
      'Why It Matters': s.why_it_matters || '',
      'Level of Evidence': s.level_of_evidence || '',
      'Grade of Recommendation': s.grade_of_recommendation || '',
      'Compliance Risk': s.compliance?.off_label_risk || (s.compliance as any)?.off_label_tone_risk_flag || '',
      'Confidence Score': s.confidence_score ?? '',
      Comment: s.comment || '',
      References: this.formatReferencesForExport(s.reference_links)
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Statements');
    XLSX.writeFile(wb, 'selected_statements.xlsx');
  }

  /**
   * Export selected statements to Word (.doc via HTML), Delphi narrative layout.
   */
  exportSelectedToWord(): void {
    const selected = this.statements.filter(s => this.selectedStatementIds.includes(s.id));
    if (selected.length === 0) return;

    const html = this.buildDelphiWordDocumentHtml(selected);
    const blob = new Blob([html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = (this.projectTitle || 'selected_statements')
      .replace(/[^a-z0-9_\- ]/gi, '_')
      .trim()
      .replace(/\s+/g, '_')
      .toLowerCase();
    a.download = `${safeTitle || 'selected_statements'}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private buildDelphiWordDocumentHtml(selected: GeneratedStatement[]): string {
    const title = this.escapeHtml(this.projectTitle?.trim() || 'Statements');
    const bodyParts: string[] = [
      `<p class="font-14"><strong>Delphi Project: ${title}</strong></p>`,
      this.wordFieldGap()
    ];

    let statementNumber = 0;
    let isFirstSection = true;
    for (const group of this.groupSelectedBySection(selected)) {
      if (!isFirstSection) {
        bodyParts.push(this.wordFieldGap(), this.wordFieldGap());
      }
      bodyParts.push(
        `<p class="font-14"><strong>SECTION - ${this.escapeHtml(group.section)}</strong></p>`
      );
      isFirstSection = false;

      let isFirstStatementInSection = true;
      for (const statement of group.statements) {
        statementNumber += 1;
        bodyParts.push(
          this.buildStatementWordHtml(statement, statementNumber, isFirstStatementInSection)
        );
        isFirstStatementInSection = false;
      }
    }

    return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8">
  <title>Delphi Project: ${title}</title>
  <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml><![endif]-->
  <style>
    body { font-family: Calibri, Arial, sans-serif; font-size: 12pt; line-height: 1.25; }
    p { margin: 0; }
    .font-14 { font-size: 14pt; }
    .font-12 { font-size: 12pt; }
    .block-label { margin: 0; }
    .block-body { margin: 0; }
    .block-body-continue { margin: 0; }
    .field-gap { margin: 0; font-size: 12pt; line-height: 12pt; mso-line-height-rule: exactly; }
    strong { font-weight: bold; }
    a { color: #0563C1; text-decoration: underline; }
  </style>
</head>
<body>
${bodyParts.join('\n')}
</body>
</html>`;
  }

  private groupSelectedBySection(
    selected: GeneratedStatement[]
  ): { section: string; statements: GeneratedStatement[] }[] {
    const orderByTitle = new Map(
      this.sections.map((s, index) => [s.title, s.order ?? index])
    );
    const orderById = new Map(this.statements.map((s, index) => [s.id, index]));
    const bySection = new Map<string, GeneratedStatement[]>();

    for (const statement of selected) {
      const key = statement.section?.trim() || 'Other';
      if (!bySection.has(key)) {
        bySection.set(key, []);
      }
      bySection.get(key)!.push(statement);
    }

    const sortStatements = (items: GeneratedStatement[]) =>
      [...items].sort(
        (a, b) => (orderById.get(a.id) ?? 0) - (orderById.get(b.id) ?? 0)
      );

    return [...bySection.keys()]
      .sort((a, b) => {
        const orderA = orderByTitle.get(a);
        const orderB = orderByTitle.get(b);
        if (orderA != null && orderB != null && orderA !== orderB) {
          return orderA - orderB;
        }
        if (orderA != null && orderB == null) return -1;
        if (orderA == null && orderB != null) return 1;
        return a.localeCompare(b);
      })
      .map(section => ({
        section,
        statements: sortStatements(bySection.get(section)!)
      }));
  }

  private buildStatementWordHtml(
    statement: GeneratedStatement,
    statementNumber: number,
    isFirstStatementInSection: boolean
  ): string {
    const parts: string[] = [];
    if (!isFirstStatementInSection) {
      parts.push(this.wordFieldGap(), this.wordFieldGap());
    }
    this.appendWordTitledBlock(
      parts,
      `<p class="font-14 block-label"><strong>Statement ${statementNumber}</strong></p>`,
      statement.statement_text || ''
    );

    const rationale = statement.rationale?.trim();
    if (rationale) {
      this.appendWordTitledBlock(parts, this.wordFieldLabel('Rationale'), rationale);
    }

    const whyItMatters = statement.why_it_matters?.trim();
    if (whyItMatters) {
      this.appendWordTitledBlock(parts, this.wordFieldLabel('Why It Matters'), whyItMatters);
    }

    const evidence = statement.level_of_evidence?.trim();
    if (evidence) {
      this.appendWordTitledBlock(parts, this.wordFieldLabel('Evidence:'), evidence);
    }

    const referenceParts = this.buildReferencesWordParagraphs(statement.reference_links);
    if (referenceParts.length > 0 && parts.length > 0) {
      parts.push(this.wordFieldGap());
    }
    parts.push(...referenceParts);

    return parts.join('\n');
  }

  private wordFieldLabel(label: string): string {
    return `<p class="font-12 block-label"><strong>${label}</strong></p>`;
  }

  private wordFieldGap(): string {
    return '<p class="field-gap">&nbsp;</p>';
  }

  private appendWordTitledBlock(parts: string[], labelHtml: string, bodyText: string): void {
    if (parts.length > 0) {
      parts.push(this.wordFieldGap());
    }
    parts.push(labelHtml);
    parts.push(`<p class="font-12 block-body">${this.escapeHtml(bodyText)}</p>`);
  }

  private buildReferencesWordParagraphs(refs: any[] | undefined): string[] {
    if (!Array.isArray(refs) || refs.length === 0) return [];

    const hyperlinks: string[] = [];
    const plainRefs: string[] = [];

    for (const ref of refs) {
      if (this.isReferenceLink(ref)) {
        const url = this.getReferenceUrl(ref);
        if (url && url !== '#') {
          const href = this.escapeHtml(url);
          hyperlinks.push(`<a href="${href}">${href}</a>`);
          continue;
        }
      }
      const text = this.formatReferenceForExport(ref);
      if (text) {
        plainRefs.push(this.escapeHtml(text));
      }
    }

    const paragraphs: string[] = [];
    if (hyperlinks.length === 0 && plainRefs.length === 0) {
      return paragraphs;
    }

    paragraphs.push(`<p class="font-12 block-label"><strong>References:</strong></p>`);

    const bodyLines = [...hyperlinks];
    if (plainRefs.length > 0) {
      bodyLines.push(plainRefs.join(' '));
    }

    bodyLines.forEach((line, index) => {
      const isLast = index === bodyLines.length - 1;
      const bodyClass = isLast ? 'block-body' : 'block-body-continue';
      paragraphs.push(`<p class="font-12 ${bodyClass}">${line}</p>`);
    });

    return paragraphs;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Get evidence badge class
   */
  getEvidenceBadgeClass(level: string): string {
    if (!level) return 'evidence-default';
    const lowerLevel = level.toLowerCase();
    if (lowerLevel.includes('1a')) return 'evidence-1a';
    if (lowerLevel.includes('1b')) return 'evidence-1b';
    if (lowerLevel.includes('2a')) return 'evidence-2a';
    if (lowerLevel.includes('2b')) return 'evidence-2b';
    if (lowerLevel.includes('3')) return 'evidence-3';
    return 'evidence-default';
  }

  /**
   * Get grade badge class
   */
  getGradeBadgeClass(grade: string): string {
    if (!grade) return 'grade-default';
    return `grade-${grade.toUpperCase().toLowerCase()}`;
  }

  /**
   * Get compliance risk badge class
   */
  getComplianceRiskClass(risk: string): string {
    if (!risk) return 'risk-default';
    return `risk-${risk}`;
  }

  isReferenceLink(ref: any): boolean {
    if (typeof ref === 'string') {
      return ref.startsWith('http://') || ref.startsWith('https://');
    }
    const refId: string = ref?.ref_id || '';
    return !!(
      ref?.url || ref?.link || ref?.href ||
      refId.startsWith('pmid:') || refId.startsWith('doi:') ||
      ref?.pmid || ref?.doi
    );
  }

  getReferenceUrl(ref: any): string {
    if (typeof ref === 'string') return ref;
    if (ref?.url) return ref.url;
    if (ref?.link) return ref.link;
    if (ref?.href) return ref.href;
    const refId: string = ref?.ref_id || '';
    if (refId.startsWith('pmid:')) {
      return `https://pubmed.ncbi.nlm.nih.gov/${refId.replace('pmid:', '')}`;
    }
    if (refId.startsWith('doi:')) {
      return `https://doi.org/${refId.replace('doi:', '')}`;
    }
    if (ref?.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${ref.pmid}`;
    if (ref?.doi) return `https://doi.org/${ref.doi}`;
    return '#';
  }

  getReferenceLabel(ref: any): string {
    if (typeof ref === 'string') return ref;
    return ref?.title || ref?.label || ref?.text || ref?.ref_id || ref?.url || ref?.link || '';
  }

  getReferenceDetail(ref: any): string {
    if (typeof ref === 'string') return ref;
    const parts: string[] = [];
    if (ref?.title) parts.push(ref.title);
    if (ref?.authors) parts.push(ref.authors);
    if (ref?.year) parts.push(String(ref.year));
    if (ref?.source) parts.push(ref.source);
    if (ref?.ref_id && !ref?.title) parts.push(ref.ref_id);
    return parts.join(' — ') || JSON.stringify(ref);
  }

  /** Plain-text reference line for Excel/CSV export (label + URL when applicable). */
  formatReferenceForExport(ref: any): string {
    if (this.isReferenceLink(ref)) {
      const url = this.getReferenceUrl(ref);
      const label = this.getReferenceLabel(ref);
      if (!url || url === '#') {
        return label || this.getReferenceDetail(ref);
      }
      if (!label || label === url) {
        return url;
      }
      return `${label} (${url})`;
    }
    return this.getReferenceDetail(ref);
  }

  formatReferencesForExport(refs: any[] | undefined): string {
    if (!Array.isArray(refs) || refs.length === 0) return '';
    return refs
      .map(r => this.formatReferenceForExport(r))
      .filter(Boolean)
      .join('; ');
  }

  /**
   * Get confidence score color
   */
  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'high';
    if (score >= 0.6) return 'medium';
    return 'low';
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.selectedSection = 'all';
    this.selectedEvidenceLevel = 'all';
    this.selectedComplianceRisk = 'all';
    this.searchText = '';
    this.showOnlySelected = false;
  }
}
